import React, { Suspense, lazy } from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
    useNavigate,
} from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth, isSuporteRole } from "@/lib/AuthContext";
import Layout from "@/components/Layout";
// Login fica eager — sem ele não há nada para mostrar a utilizadores não autenticados
import Login from "@/pages/Login";
// Todas as outras páginas são lazy-loaded — só descarregam quando o utilizador
// navega para essa rota, reduzindo drasticamente o JS inicial.
const DashboardAdmin = lazy(() => import("@/pages/DashboardAdmin"));
const Vendas = lazy(() => import("@/pages/Vendas"));
const NovaVenda = lazy(() => import("@/pages/v2/NovaVendaV2"));
const Instalacoes = lazy(() => import("@/pages/Instalacoes"));
const Configuracoes = lazy(() => import("@/pages/Configuracoes"));
const Fontes = lazy(() => import("@/pages/Fontes"));
const Parceiros = lazy(() => import("@/pages/Parceiros"));
const Recebimentos = lazy(() => import("@/pages/Recebimentos"));
const Alertas = lazy(() => import("@/pages/Alertas"));
const TarefasAiLog = lazy(() => import("@/pages/TarefasAiLog"));
const PosVenda = lazy(() => import("@/pages/PosVenda"));
const PosVendaConfig = lazy(() => import("@/pages/PosVendaConfig"));
const PosVendaFeedback = lazy(() => import("@/pages/PosVendaFeedback"));
const PosVendaTrainChat = lazy(() => import("@/pages/PosVendaTrainChat"));
const RelatorioTrabalho = lazy(() => import("@/pages/RelatorioTrabalho"));
const Comparativo = lazy(() => import("@/pages/Comparativo"));
const Localizacao = lazy(() => import("@/pages/Localizacao"));
const Conversas = lazy(() => import("@/pages/Conversas"));
const LeadsParceiros = lazy(() => import("@/pages/LeadsParceiros"));
const PublicLeadForm = lazy(() => import("@/pages/PublicLeadForm"));
const PublicPartnerDashboard = lazy(() => import("@/pages/PublicPartnerDashboard"));
const Propostas = lazy(() => import("@/pages/Propostas"));
const PropostaEditor = lazy(() => import("@/pages/PropostaEditor"));
const PublicProposta = lazy(() => import("@/pages/PublicProposta"));
const PropostaShortRedirect = lazy(() => import("@/pages/PropostaShortRedirect"));
const Feedback = lazy(() => import("@/pages/Feedback"));
const PublicFeedbackForm = lazy(() => import("@/pages/PublicFeedbackForm"));
// v2 (redesign Linear/Superhuman)
const V2Hoje = lazy(() => import("@/pages/v2/Hoje"));
const V2Clientes = lazy(() => import("@/pages/v2/Clientes"));
const V2Pipeline = lazy(() => import("@/pages/v2/Pipeline"));
const V2Financeiro = lazy(() => import("@/pages/v2/Financeiro"));
const V2Instalacoes = lazy(() => import("@/pages/v2/InstalacoesV2"));
const V2Producao = lazy(() => import("@/pages/v2/ProducaoMes"));
const EstadoSistema = lazy(() => import("@/pages/EstadoSistema"));
import ShellV2 from "@/components/v2/Shell";
import { Toaster } from "@/components/ui/sonner";
import TodayPopup from "@/components/TodayPopup";

// Loader minimalista para enquanto a página carrega
const PageLoader = () => (
    <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
    </div>
);

// Whitelist explícita de rotas ADMIN (autenticadas). Qualquer outra rota
// é tratada como pública e NÃO renderiza overlays/popups internos.

// Pré-carrega as páginas mais usadas em idle (quando o browser está parado),
// para que a navegação após login seja instantânea (sem spinner).
if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(() => {
        import("@/pages/DashboardAdmin");
        import("@/pages/Conversas");
        import("@/pages/Vendas");
        import("@/pages/Instalacoes");
    });
} else if (typeof window !== "undefined") {
    // Fallback para Safari mobile que não tem requestIdleCallback
    setTimeout(() => {
        import("@/pages/DashboardAdmin");
        import("@/pages/Conversas");
        import("@/pages/Vendas");
    }, 2000);
}
// Isto previne fuga de componentes admin nas zonas /p/, /proposta/, /:short_id, etc.
const ADMIN_PATHS_EXACT = new Set([
    "/", "/vendas", "/nova-venda", "/instalacoes", "/fontes", "/parceiros",
    "/recebimentos", "/alertas", "/tarefas", "/configuracoes",
    "/pos-venda", "/pos-venda/configuracoes", "/pos-venda/treino", "/pos-venda/treino-chat",
    "/relatorio", "/conversas", "/leads-parceiros", "/propostas", "/propostas/nova",
    "/hoje", "/clientes", "/pipeline", "/financeiro", "/instalacoes-v2",
]);
const ADMIN_PATH_PREFIXES = ["/editar-venda/"];

// Rotas do layout antigo com equivalente no layout novo (usado para o perfil Suporte).
const SUPORTE_V2_REDIRECTS = {
    "/": "/hoje",
    "/vendas": "/clientes",
    "/instalacoes": "/instalacoes-v2",
    "/tarefas": "/instalacoes-v2",
};

const isAdminPath = (pathname) => {
    if (!pathname) return false;
    if (ADMIN_PATHS_EXACT.has(pathname)) return true;
    return ADMIN_PATH_PREFIXES.some((p) => pathname.startsWith(p));
};

// Wrapper que renderiza popups/alertas globais APENAS em rotas Admin.
// Critico: protege contra fuga de UI admin nas zonas /p/, /proposta/, /:short_id e /login.
const AdminOnlyOverlays = () => {
    const location = useLocation();
    const { user } = useAuth();
    // Dupla camada: exige rota admin E utilizador autenticado.
    if (!isAdminPath(location.pathname)) return null;
    if (!user || user === false) return null;
    return <TodayPopup />;
};

const Protected = ({ children, adminOnly = false }) => {
    const { user, booting } = useAuth();
    const location = useLocation();
    if (booting) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f9f9f9]">
                <div className="text-center">
                    <div className="w-10 h-10 border-2 border-zinc-200 border-t-zinc-900 rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm text-zinc-500 font-mono uppercase tracking-wider">A carregar...</p>
                </div>
            </div>
        );
    }
    if (!user) {
        // Se for um parceiro com PWA instalada, redirecciona para o dashboard dele
        try {
            const pd = JSON.parse(localStorage.getItem("partner_dashboard") || "null");
            if (pd?.slug && pd?.token && !location.pathname.startsWith("/p/")) {
                return <Navigate to={`/p/${pd.slug}/leads?token=${encodeURIComponent(pd.token)}`} replace />;
            }
        } catch (_) { /* ignore */ }
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
    // Suporte trabalha EXCLUSIVAMENTE no layout novo: rotas antigas com equivalente v2 redireccionam;
    // as restantes (mesmas permissões) abrem dentro do Shell v2 — nunca no Layout antigo.
    if (isSuporteRole(user.role)) {
        if (user.perfil === "posvenda" && ["/nova-venda", "/conversas", "/producao"].includes(location.pathname)) return <Navigate to="/hoje" replace />;
        const target = SUPORTE_V2_REDIRECTS[location.pathname];
        if (target) return <Navigate to={`${target}${location.search || ""}`} replace />;
        return <ShellV2>{children}</ShellV2>;
    }
    return <Layout><GestorGuard>{children}</GestorGuard></Layout>;
};

// Novo Protected para rotas v2 (Shell Linear/Superhuman)
const ProtectedV2 = ({ children, adminOnly = false }) => {
    const { user, booting } = useAuth();
    const location = useLocation();
    if (booting) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
                <div className="w-8 h-8 border-2 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
            </div>
        );
    }
    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
    if (adminOnly && user.role !== "admin") return <Navigate to="/hoje" replace />;
    return <ShellV2>{children}</ShellV2>;
};

const Home = () => {
    const { user } = useAuth();
    if (user?.role === "gestor") return <Navigate to="/leads-parceiros" replace />;
    if (user?.role === "admin") return <Navigate to="/hoje" replace />;
    return <Navigate to="/hoje" replace />;
};

// HOC: previne renderizar conteúdo em rotas não-parceiros para gestores.
// O redirect é feito por <Navigate>, e este HOC apenas impede que os children
// renderizem (e chamem APIs 403) durante a transição.
const GestorGuard = ({ children }) => {
    const { user } = useAuth();
    const location = useLocation();
    if (user?.role === "gestor" && !location.pathname.startsWith("/leads-parceiros")) {
        return <Navigate to="/leads-parceiros" replace />;
    }
    return children;
};

/**
 * Catch-all: para utilizadores autenticados redireciona para "/", mas para
 * visitantes anónimos (clientes que entram pelo link da proposta) mostra uma
 * página neutra "Não encontrado" — SEM expor login ou áreas admin.
 */
const NotFoundOrHome = () => {
    const { user, booting } = useAuth();
    if (booting) return null;
    if (user) return <Navigate to="/" replace />;
    return (
        <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white px-6 text-center" data-testid="public-not-found">
            <div className="text-6xl mb-3">🔍</div>
            <h1 className="text-2xl font-black text-zinc-200">Página não disponível</h1>
            <p className="text-sm text-zinc-500 mt-2 max-w-sm">Verifica se o link da proposta está correcto. Se foi o Salvador a enviar, abre o link directamente da mensagem.</p>
        </div>
    );
};

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Suspense fallback={<PageLoader />}>
                        <Routes>
                            <Route path="/login" element={<Login />} />
                        {/* Rotas públicas — formulário de lead + dashboard parceiro */}
                        <Route path="/p/:slug" element={<PublicLeadForm />} />
                        <Route path="/p/:slug/leads" element={<PublicPartnerDashboard />} />
                        <Route path="/f/:token" element={<PublicFeedbackForm />} />
                        {/* Alias: links antigos usam /feedback/{token} — mantido para compat */}
                        <Route path="/feedback/:token" element={<PublicFeedbackForm />} />
                        <Route
                            path="/feedback"
                            element={
                                <Protected>
                                    <Feedback />
                                </Protected>
                            }
                        />
                        <Route
                            path="/"
                            element={
                                <Protected>
                                    <Home />
                                </Protected>
                            }
                        />
                        <Route
                            path="/vendas"
                            element={
                                <Protected>
                                    <Vendas />
                                </Protected>
                            }
                        />
                        <Route
                            path="/nova-venda"
                            element={
                                <Protected>
                                    <NovaVenda />
                                </Protected>
                            }
                        />
                        <Route
                            path="/editar-venda/:id"
                            element={
                                <Protected>
                                    <NovaVenda />
                                </Protected>
                            }
                        />
                        <Route
                            path="/instalacoes"
                            element={
                                <Protected>
                                    <Instalacoes />
                                </Protected>
                            }
                        />
                        <Route
                            path="/fontes"
                            element={
                                <Protected>
                                    <Fontes />
                                </Protected>
                            }
                        />
                        <Route
                            path="/parceiros"
                            element={
                                <Protected>
                                    <Parceiros />
                                </Protected>
                            }
                        />
                        <Route
                            path="/recebimentos"
                            element={
                                <Protected>
                                    <Recebimentos />
                                </Protected>
                            }
                        />
                        <Route
                            path="/alertas"
                            element={
                                <Protected>
                                    <Alertas />
                                </Protected>
                            }
                        />
                        <Route path="/tarefas" element={<Navigate to="/instalacoes" replace />} />
                        <Route
                            path="/tarefas-ai-log"
                            element={
                                <Protected>
                                    <TarefasAiLog />
                                </Protected>
                            }
                        />
                        <Route
                            path="/configuracoes"
                            element={
                                <Protected adminOnly>
                                    <Configuracoes />
                                </Protected>
                            }
                        />
                        <Route
                            path="/pos-venda"
                            element={
                                <Protected>
                                    <PosVenda />
                                </Protected>
                            }
                        />
                        <Route
                            path="/pos-venda/configuracoes"
                            element={
                                <Protected>
                                    <PosVendaConfig />
                                </Protected>
                            }
                        />
                        <Route
                            path="/pos-venda/treino"
                            element={
                                <Protected>
                                    <PosVendaFeedback />
                                </Protected>
                            }
                        />
                        <Route
                            path="/pos-venda/treino-chat"
                            element={
                                <Protected>
                                    <PosVendaTrainChat />
                                </Protected>
                            }
                        />
                        <Route
                            path="/relatorio"
                            element={
                                <Protected>
                                    <RelatorioTrabalho />
                                </Protected>
                            }
                        />
                        <Route
                            path="/comparativo"
                            element={
                                <Protected adminOnly>
                                    <Comparativo />
                                </Protected>
                            }
                        />
                        <Route
                            path="/localizacao"
                            element={
                                <Protected>
                                    <Localizacao />
                                </Protected>
                            }
                        />
                        <Route
                            path="/conversas"
                            element={
                                <Protected>
                                    <Conversas />
                                </Protected>
                            }
                        />
                        <Route
                            path="/leads-parceiros"
                            element={
                                <Protected>
                                    <LeadsParceiros />
                                </Protected>
                            }
                        />
                        <Route
                            path="/propostas"
                            element={
                                <Protected adminOnly>
                                    <Propostas />
                                </Protected>
                            }
                        />
                        <Route
                            path="/propostas/nova"
                            element={
                                <Protected adminOnly>
                                    <PropostaEditor />
                                </Protected>
                            }
                        />
                        <Route path="/proposta/:public_id" element={<PublicProposta />} />
                        {/* ============================ v2 Redesign ============================ */}
                        <Route
                            path="/hoje"
                            element={
                                <ProtectedV2>
                                    <V2Hoje />
                                </ProtectedV2>
                            }
                        />
                        <Route
                            path="/clientes"
                            element={
                                <ProtectedV2>
                                    <V2Clientes />
                                </ProtectedV2>
                            }
                        />
                        <Route
                            path="/pipeline"
                            element={
                                <ProtectedV2>
                                    <V2Pipeline />
                                </ProtectedV2>
                            }
                        />
                        <Route
                            path="/financeiro"
                            element={
                                <ProtectedV2 adminOnly>
                                    <V2Financeiro />
                                </ProtectedV2>
                            }
                        />
                        <Route
                            path="/instalacoes-v2"
                            element={
                                <ProtectedV2>
                                    <V2Instalacoes />
                                </ProtectedV2>
                            }
                        />
                        <Route
                            path="/producao"
                            element={
                                <ProtectedV2>
                                    <V2Producao />
                                </ProtectedV2>
                            }
                        />
                        <Route
                            path="/estado-sistema"
                            element={
                                <ProtectedV2>
                                    <EstadoSistema />
                                </ProtectedV2>
                            }
                        />
                        {/* Quando acedem via proposta.aderecomsalvador.pt/XXXX directamente */}
                        <Route path="/:short_id" element={<PropostaShortRedirect />} />
                        <Route path="*" element={<NotFoundOrHome />} />
                        </Routes>
                    </Suspense>
                    <Toaster position="top-right" richColors closeButton />
                    <AdminOnlyOverlays />
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
